import { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useTimeEntriesCache } from "@/hooks/useTimeEntriesCache";
import axios from "axios";

export default function AuthCallback({ redirectDelay = 1500 }: { redirectDelay?: number }) {
  const [status, setStatus] = useState<"loading" | "success" | "error">("loading");
  const [message, setMessage] = useState("");
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { checkAuth } = useAuth();
  const { loadAllEntries } = useTimeEntriesCache();
  const hasProcessed = useRef(false);

  // Extract search parameters once to avoid infinite re-renders
  const token = searchParams.get("token");
  const userParam = searchParams.get("user");

  // Memoize the callback function to prevent infinite loops
  const handleCallback = useCallback(async () => {
    try {
      // Only handle OIDC flow with token and user in URL parameters
      if (token && userParam) {
        try {
          const user = JSON.parse(decodeURIComponent(userParam));
          localStorage.setItem("authToken", token);
          localStorage.setItem("user", JSON.stringify(user));
          axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
          await checkAuth();
          // Load entries cache after successful authentication
          loadAllEntries();
          if (redirectDelay === 0) {
            navigate("/dashboard");
          } else {
            setTimeout(() => {
              navigate("/dashboard");
            }, redirectDelay);
          }
        } catch {
          setStatus("error");
          setMessage("Invalid authentication data. Please try logging in again.");
        }
      } else {
        setStatus("error");
        setMessage("No authentication data found. Please try logging in again.");
      }
    } catch {
      setStatus("error");
      setMessage("Authentication failed. Please try again.");
    }
  }, [token, userParam, checkAuth, loadAllEntries, navigate, redirectDelay]);

  useEffect(() => {
    // Prevent multiple executions
    if (hasProcessed.current) {
      return;
    }

    hasProcessed.current = true;
    handleCallback();
  }, [handleCallback]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="max-w-md w-full space-y-8">
        {status === "error" && (
          <div className="text-center">
            <h2 className="mt-6 text-3xl font-extrabold text-gray-900">
              Authentication Failed
            </h2>
            <p className="mt-2 text-sm text-gray-600">
              {message}
            </p>
          </div>
        )}
        {status !== "error" && (
          <div className="flex justify-center">
            <div data-testid="loading-spinner" className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        )}
        {status === "error" && (
          <div className="mt-4">
            <button
              onClick={() => navigate("/login")}
              className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Back to Login
            </button>
          </div>
        )}
      </div>
    </div>
  );
} 